//  AvailablePrograms  -  Shows  all  active  programs  available  to  the  user
//  Users  navigate  here  from  "Create  New  Submission"  button

import  React,  {  useState,  useEffect  }  from  'react';
import  {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Divider,
}  from  '@mui/material';
import  {
    Add  as  AddIcon,
    ListAlt  as  ListIcon,
    WalletSharp  as  WalletIcon,
    CalendarMonth  as  CalendarIcon,
    TrendingUp  as  TrendingIcon,
}  from  '@mui/icons-material';
import  {  useNavigate  }  from  'react-router-dom';
import  {  programsAPI  }  from  '../../services/api';
import  dayjs  from  'dayjs';

const  AvailablePrograms:  React.FC  =  ()  =>  {
    const  [programs,  setPrograms]  =  useState<any[]>([]);
    const  [loading,  setLoading]  =  useState(true);
    const  [error,  setError]  =  useState<string  |  null>(null);
    const  navigate  =  useNavigate();

    useEffect(()  =>  {
        fetchActivePrograms();
    },  []);

    const  fetchActivePrograms  =  async  ()  =>  {
        try  {
            setLoading(true);
            setError(null);
            const  data  =  await  programsAPI.getActivePrograms();
            setPrograms(data);
        }  catch  (err:  any)  {
            setError(err.response?.data?.message  ||  'Failed  to  load  programs');
            console.error('Error  fetching  programs:',  err);
        }  finally  {
            setLoading(false);
        }
    };

    if  (loading)  {
        return  (
            <Box  display="flex"  justifyContent="center"  alignItems="center"  minHeight="400px">
                <CircularProgress  />
            </Box>
        );
    }

    if  (error)  {
        return  (
            <Alert  severity="error"  sx={{  mt:  2  }}>
                {error}
            </Alert>
        );
    }

    if  (programs.length  ===  0)  {
        return  (
            <Alert  severity="warning"  sx={{  mt:  2  }}>
                No  active  programs  available.  Please  contact  your  administrator.
            </Alert>
        );
    }

    return  (
        <Box>
            <Typography  variant="h4"  gutterBottom>
                Available  Programs
            </Typography>
            <Typography  variant="body1"  color="text.secondary"  paragraph>
                Select  a  program  to  create  a  new  expense  submission
            </Typography>

            <Grid  container  spacing={3}  sx={{  mt:  2  }}>
                {/*  Available  Programs  */}
                <Grid  item  xs={12}  md={8}>
                    <Typography  variant="h6"  gutterBottom>
                        Active  Programs
                    </Typography>
                    
                    <Grid  container  spacing={2}>
                        {programs.map((program)  =>  (
                            <Grid  item  xs={12}  key={program.programId}>
                                <Card
                                    sx={{
                                        border:  program.isGeneral  ?  '2px  solid'  :  undefined,
                                        borderColor:  program.isGeneral  ?  'primary.main'  :  undefined,
                                    }}
                                >
                                    <CardContent>
                                        <Box  display="flex"  justifyContent="space-between"  alignItems="start"  mb={2}>
                                            <Box  display="flex"  alignItems="center">
                                                <WalletIcon  color="primary"  sx={{  fontSize:  32,  mr:  2  }}  />
                                                <Box>
                                                    <Typography  variant="h6">
                                                        {program.programName}
                                                    </Typography>
                                                    {program.isGeneral  &&  (
                                                        <Chip  label="Always  Available"  color="success"  size="small"  sx={{  mt:  0.5  }}  />
                                                    )}
                                                </Box>
                                            </Box>
                                            <Chip
                                                label={program.canSubmit  ?  'Active'  :  'Unavailable'}
                                                color={program.canSubmit  ?  'success'  :  'default'}
                                                size="small"
                                            />
                                        </Box>

                                        {program.description  &&  (
                                            <Typography  variant="body2"  color="text.secondary"  paragraph>
                                                {program.description}
                                            </Typography>
                                        )}

                                        <Divider  sx={{  my:  2  }}  />

                                        <Grid  container  spacing={2}>
                                            {!program.isGeneral  &&  (
                                                <>
                                                    <Grid  item  xs={6}>
                                                        <Box  display="flex"  alignItems="center"  gap={0.5}>
                                                            <CalendarIcon  fontSize="small"  color="action"  />
                                                            <Typography  variant="caption"  color="text.secondary">
                                                                Ends  {dayjs(program.endDate).format('MMM  D,  YYYY')}
                                                            </Typography>
                                                        </Box>
                                                    </Grid>
                                                    <Grid  item  xs={6}>
                                                        <Typography  variant="caption"  color="text.secondary">
                                                            {program.daysRemaining}  days  remaining
                                                        </Typography>
                                                    </Grid>
                                                </>
                                            )}

                                            {program.allowance  &&  (
                                                <Grid  item  xs={6}>
                                                    <Typography  variant="caption"  color="text.secondary">
                                                        Allowance:  ${program.allowance.toFixed(2)}
                                                    </Typography>
                                                    {program.totalSpent  !==  undefined  &&  (
                                                        <Typography  variant="caption"  display="block"  color="primary.main">
                                                            Spent:  ${program.totalSpent.toFixed(2)}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            )}

                                            {program.maxSubmissionsPerUser  &&  (
                                                <Grid  item  xs={6}>
                                                    <Box  display="flex"  alignItems="center"  gap={0.5}>
                                                        <TrendingIcon  fontSize="small"  color="action"  />
                                                        <Typography  variant="caption"  color="text.secondary">
                                                            {program.userSubmissionCount}  of  {program.maxSubmissionsPerUser}  submissions  used
                                                        </Typography>
                                                    </Box>
                                                    {program.remainingSubmissions  !==  null  &&  program.remainingSubmissions  >  0  &&  (
                                                        <Typography  variant="caption"  display="block"  color="success.main">
                                                            {program.remainingSubmissions}  submission{program.remainingSubmissions  !==  1  ?  's'  :  ''}  remaining
                                                        </Typography>
                                                    )}
                                                </Grid>
                                            )}
                                        </Grid>

                                        {program.expenseCategories  &&  program.expenseCategories.length  >  0  &&  (
                                            <Box  mt={2}>
                                                <Typography  variant="caption"  color="text.secondary"  display="block"  gutterBottom>
                                                    Categories:
                                                </Typography>
                                                <Box  display="flex"  flexWrap="wrap"  gap={0.5}>
                                                    {program.expenseCategories.map((category:  string)  =>  (
                                                        <Chip
                                                            key={category}
                                                            label={category}
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>

                                    <CardActions  sx={{  px:  2,  pb:  2  }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon  />}
                                            onClick={()  =>  navigate('/submissions/create',  {  state:  {  programId:  program.programId  }  })}
                                            disabled={!program.canSubmit}
                                            fullWidth
                                        >
                                            {program.canSubmit  ?  'Create  Submission'  :  'Not  Available'}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {/*  Quick  Guide  Card  */}
                <Grid  item  xs={12}  md={4}>
                    <Card  sx={{  bgcolor:  'primary.main',  color:  'white',  mb:  2  }}>
                        <CardContent>
                            <Typography  variant="h6"  gutterBottom>
                                Quick  Start  Guide
                            </Typography>
                            <Typography  variant="body2"  paragraph>
                                1.  Select  a  program  and  click  "Create  Submission"
                            </Typography>
                            <Typography  variant="body2"  paragraph>
                                2.  Choose  the  expense  period
                            </Typography>
                            <Typography  variant="body2"  paragraph>
                                3.  Add  your  receipts  with  details
                            </Typography>
                            <Typography  variant="body2">
                                4.  Submit  for  manager  approval
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <Typography  variant="h6"  gutterBottom>
                                My  Submissions
                            </Typography>
                            <Typography  variant="body2"  color="text.secondary"  paragraph>
                                View  and  track  all  your  expense  submissions
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<ListIcon  />}
                                onClick={()  =>  navigate('/submissions')}
                                fullWidth
                            >
                                View  All  Submissions
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export  default  AvailablePrograms;